import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { loadCommissions, updateCommissions, computeCommission } from '../actionCreators';
import { addCommission, deleteCommission } from '../actionCreators';
import { getSubsidies, getCommissionsFlags, getOfferCurrency } from '../selectors';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import SubsidiesComponent from './SubsidiesComponent';
import flow from 'lodash.flow';
import useEventCallback from 'utils/hooks/useEventCallback';
import { getCommissionsValidationRules } from '../rules';
import { subsidiesCollectionValidator } from '../validators';
import { useValidation } from '@totalsoft/pure-validations-react';

const SubsidiesContainer = ({ documentId, siteId, numberOfSubsidies }) => {
  const [state, setState] = useState({
    subsidies: [],
    lastAddedItemIndex: 0,
    showModal: false,
    subsidyListIsDirty: false
  });

  const [expandedSubsidyCard, setExpandedSubsidyCard] = useState(false);
  const [calcInProgress, setCalcInProgress] = useState({
    inProgress: false,
    index: null
  });
  const [itemRemoved, setItemRemoved] = useState(false);

  const commissionRules = useMemo(() => getCommissionsValidationRules(state.subsidies), [state.subsidies]);
  const [subsidiesCollectionValidation, validateSubsidiesCollection] = useValidation(subsidiesCollectionValidator);
  const dispatch = useDispatch();

  const key = `${documentId}-${siteId}`;
  const { loaded, computed, saving } = useSelector(
    state => getCommissionsFlags(state.currentOffer.commissions, state.entities, key),
    shallowEqual
  );

  const subsidies = useSelector(state => getSubsidies(state.currentOffer.commissions, state.entities, key), shallowEqual);

  const { currencyId, currencyCode } = useSelector(state => getOfferCurrency(state.currentOffer.header), shallowEqual);

  useEffect(() => {
    if (loaded || !expandedSubsidyCard) return;
    dispatch(loadCommissions(documentId, siteId));
  }, [documentId, siteId, loaded, dispatch, expandedSubsidyCard]);

  useEffect(() => {
    if (!loaded) return;
    setState(prev => ({
      ...prev,
      subsidies: subsidies.map(editableDocument.create)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    if (!computed || !computed.isSubsidyComputed) return;

    const computedSubsidy = editableDocument.create({ ...subsidies[computed.index] });
    setState(prev => ({
      ...prev,
      subsidies: collection.update(computed.index, editableDocument.setDirty(computedSubsidy), prev.subsidies)
    }));
  }, [computed, subsidies]);

  useEffect(() => {
    validateSubsidiesCollection(state.subsidies);
  }, [state.subsidies, validateSubsidiesCollection]);

  const changeItemProperty = useCallback(
    (index, prop, value) => {
      if (state.subsidies && state.subsidies.length < 1) return;
      if (prop === 'commissionPercent') {
        setState(prev => ({
          ...prev,
          subsidyListIsDirty: true,
          subsidies: collection.update(
            index,
            flow(editableDocument.update(prop, value, null), editableDocument.validate(commissionRules, false)),
            prev.subsidies
          )
        }));
        if (state.subsidies[index].document.calculationMethodId && !itemRemoved) {
          setCalcInProgress({ inProgress: true, index });
          dispatch(
            computeCommission(
              state.subsidies[index].document,
              state.subsidies[index].document.commissionId,
              {
                calculationMethodId: state.subsidies[index].document.calculationMethodId,
                calculationMethodName: state.subsidies[index].document.calculationMethodName
              },
              documentId,
              siteId,
              value,
              state.subsidies[index].document.commissionAmount,
              true,
              index,
              true,
              () => {
                setCalcInProgress({ inProgress: false, index });
              }
            )
          );
        }
      } else if (prop === 'commissionAmount') {
        setState(prev => ({
          ...prev,
          subsidyListIsDirty: true,
          subsidies: collection.update(
            index,
            flow(editableDocument.update(prop, value, null), editableDocument.validate(commissionRules, false)),
            prev.subsidies
          )
        }));
        if (state.subsidies[index].document.calculationMethodId && !itemRemoved) {
          setCalcInProgress({ inProgress: true, index });
          dispatch(
            computeCommission(
              state.subsidies[index].document,
              state.subsidies[index].document.commissionId,
              {
                calculationMethodId: state.subsidies[index].document.calculationMethodId,
                calculationMethodName: state.subsidies[index].document.calculationMethodName
              },
              documentId,
              siteId,
              state.subsidies[index].document.commissionPercent,
              value,
              false,
              index,
              true,
              () => {
                setCalcInProgress({ inProgress: false, index });
              }
            )
          );
        }
      } else if (prop === 'partner') {
        setState(prev => ({
          ...prev,
          subsidyListIsDirty: true,
          subsidies: collection.update(
            index,
            flow(
              editableDocument.update('partnerId', value.dealerId, null),
              editableDocument.update('partnerName', value.name, null),
              editableDocument.validate(commissionRules, false)
            ),
            prev.subsidies
          )
        }));
      } else if (prop === 'calculationMethod') {
        if (state.subsidies[index].document.commissionPercent && !itemRemoved) {
          setCalcInProgress({ inProgress: true, index });
          dispatch(
            computeCommission(
              state.subsidies[index].document,
              value.commissionId ? value.commissionId : state.subsidies[index].document.commissionId,
              { calculationMethodId: value.calculationMethodId, calculationMethodName: value.calculationMethodName },
              documentId,
              siteId,
              value.calculationMethodId ? state.subsidies[index].document.commissionPercent : null,
              value.calculationMethodId ? state.subsidies[index].document.commissionAmount : null,
              true,
              index,
              true,
              () => {
                setCalcInProgress({ inProgress: false, index });
              }
            )
          );
        }
        setState(prev => ({
          ...prev,
          subsidyListIsDirty: true,
          subsidies: collection.update(
            index,
            flow(
              editableDocument.update('calculationMethodId', value.calculationMethodId, null),
              editableDocument.update('calculationMethodName', value.calculationMethodName, null),
              editableDocument.validate(commissionRules, false)
            ),
            prev.subsidies
          )
        }));
      } else {
        setState(prev => ({
          ...prev,
          subsidyListIsDirty: true,
          subsidies: collection.update(
            index,
            flow(editableDocument.update(prop, value, null), editableDocument.validate(commissionRules, false)),
            prev.subsidies
          )
        }));
      }
      setItemRemoved(false);
    },
    [commissionRules, dispatch, documentId, siteId, state.subsidies, itemRemoved]
  );

  const saveSubsidies = useEventCallback(() => {
    const validationList = state.subsidies.map(x => editableDocument.validate(commissionRules, false, x));
    if (validationList.some(x => !x.validationInfo.isValid)) {
      setState(prev => ({
        ...prev,
        subsidies: prev.subsidies
      }));
      return;
    }
    if (!validateSubsidiesCollection(state.subsidies)) return;

    const subsidiesToSave = state.subsidies.map(x => x.document);
    dispatch(
      updateCommissions(documentId, siteId, subsidiesToSave, true, () => {
        setState(prev => ({
          ...prev,
          subsidies: prev.subsidies.map(x => {
            return editableDocument.resetDirtyInfo(x);
          }),
          subsidyListIsDirty: false
        }));
      })
    );
  }, [state.subsidies]);

  const addNewItem = useEventCallback(() => {
    const initialSubsidy = {
      documentId,
      siteId,
      documentCommissionId: state.lastAddedItemIndex - 1,
      commissionAmount: null,
      commissionPercent: null,
      currencyId: currencyId,
      currencyCode: currencyCode,
      calculationMethodId: null,
      isSubsidy: true
    };
    dispatch(addCommission(initialSubsidy));
    setState(prev => ({
      ...prev,
      subsidies: collection.insert(prev.subsidies.length, editableDocument.create(initialSubsidy), prev.subsidies),
      lastAddedItemIndex: prev.lastAddedItemIndex - 1,
      subsidyListIsDirty: true
    }));
  }, []);

  const removeItem = useEventCallback(idx => {
    if (idx === -1) return;
    dispatch(deleteCommission(documentId, siteId, state.subsidies[idx]));
    setState(prev => ({
      ...prev,
      subsidies: collection.remove(idx, prev.subsidies),
      subsidyListIsDirty: true
    }));
    setItemRemoved(true);
  }, []);

  return (
    <SubsidiesComponent
      commissions={state.subsidies}
      numberOfSubsidies={numberOfSubsidies}
      onSave={saveSubsidies}
      onAdd={addNewItem}
      onRemove={removeItem}
      saving={saving.subsidies}
      onPropertyChange={changeItemProperty}
      listIsDirty={state.subsidyListIsDirty}
      calcInProgress={calcInProgress}
      expandedSubsidyCard={expandedSubsidyCard}
      setExpandedSubsidyCard={setExpandedSubsidyCard}
      validation={subsidiesCollectionValidation}
      loaded={loaded}
    />
  );
};

SubsidiesContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  subsidies: PropTypes.array,
  numberOfSubsidies: PropTypes.number
};

export default SubsidiesContainer;
